/**
 * THP size buttons — replace Debut dropdown with visible size tiles.
 * Keeps the native <select class="single-option-selector-*"> in sync so
 * theme.Variants continues to work (it listens for change on that select).
 * Size tiles are sorted XXS → XS → S → M → L → XL → XXL (then numeric).
 */
(function () {
  'use strict';

  var SIZEISH = /^(title|size|sizes|taille|größe|groesse|talla|misura)$/i;

  // Letter size rank (lower = earlier). Unknown letters fall after XXL.
  var LETTER_RANK = {
    XXXS: 1,
    XXS: 2,
    XS: 3,
    S: 4,
    SM: 4.5,
    M: 5,
    MD: 5,
    L: 6,
    LG: 6,
    XL: 7,
    XXL: 8,
    '2XL': 8,
    XXXL: 9,
    '3XL': 9,
    '4XL': 10,
    '5XL': 11
  };

  function prettyLabel(name) {
    if (!name) return 'Select size:';
    if (SIZEISH.test(String(name).trim())) return 'Select size:';
    return String(name).replace(/:\s*$/, '') + ':';
  }

  function normalizeSizeToken(raw) {
    var s = String(raw || '').trim();
    // Strip sold-out suffixes / extra labels: "XS - Sold out", "M / Medium"
    s = s.replace(/\s*[–—-]\s*sold\s*out.*/i, '');
    s = s.replace(/\s*\(.*\)\s*$/, '');
    s = s.split('/')[0].trim();
    return s;
  }

  function sizeSortKey(label) {
    var raw = normalizeSizeToken(label);
    var upper = raw.toUpperCase().replace(/\s+/g, '');
    if (Object.prototype.hasOwnProperty.call(LETTER_RANK, upper)) {
      return [0, LETTER_RANK[upper], upper];
    }
    // e.g. "34", "36.5", "EU 38"
    var numMatch = raw.match(/(\d+(?:\.\d+)?)/);
    if (numMatch) {
      return [1, parseFloat(numMatch[1]), upper];
    }
    // Unknown — keep stable alpha after known sizes
    return [2, 0, upper];
  }

  function compareSizes(a, b) {
    var ka = sizeSortKey(a);
    var kb = sizeSortKey(b);
    if (ka[0] !== kb[0]) return ka[0] - kb[0];
    if (ka[1] !== kb[1]) return ka[1] - kb[1];
    if (ka[2] < kb[2]) return -1;
    if (ka[2] > kb[2]) return 1;
    return 0;
  }

  function sortedOptions(select) {
    var opts = Array.prototype.slice.call(select.options);
    opts.sort(function (oa, ob) {
      return compareSizes(oa.textContent, ob.textContent);
    });
    return opts;
  }

  function findProductJson(sectionRoot) {
    if (!sectionRoot) return null;
    var sectionId = sectionRoot.getAttribute('data-section-id');
    var el =
      (sectionId && document.getElementById('ProductJson-' + sectionId)) ||
      sectionRoot.querySelector('script[id^="ProductJson-"]') ||
      document.querySelector('script[id^="ProductJson-"]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function optionKey(indexAttr) {
    // data-index is "option1" | "option2" | "option3"
    return indexAttr || 'option1';
  }

  function currentSelections(form) {
    var selected = {};
    form.querySelectorAll('select.single-option-selector').forEach(function (sel) {
      selected[optionKey(sel.getAttribute('data-index'))] = sel.value;
    });
    return selected;
  }

  function valueAvailable(product, optionIndexAttr, value, selections) {
    if (!product || !product.variants) return true;
    var key = optionKey(optionIndexAttr);
    return product.variants.some(function (v) {
      if (!v.available) return false;
      if (v[key] !== value) return false;
      return Object.keys(selections).every(function (k) {
        if (k === key) return true;
        return !selections[k] || v[k] === selections[k];
      });
    });
  }

  function anyVariantHasValue(product, optionIndexAttr, value) {
    if (!product || !product.variants) return true;
    var key = optionKey(optionIndexAttr);
    return product.variants.some(function (v) {
      return v.available && v[key] === value;
    });
  }

  function buildPicker(select) {
    if (!select || select.dataset.thpSizeEnhanced === '1') return;
    var wrapper = select.closest('.selector-wrapper') || select.parentElement;
    if (!wrapper) return;

    var form = select.closest('form[data-product-form], form.product-form, .product-form') || wrapper.closest('[data-section-type="product"]');
    var sectionRoot = select.closest('[data-section-type="product"]') || document.getElementById('ProductSection-product-template');
    var product = findProductJson(sectionRoot);

    var originalLabel = wrapper.querySelector('label');
    var optionName = originalLabel ? originalLabel.textContent.trim() : 'Size';

    var picker = document.createElement('div');
    picker.className = 'thp-size-picker';
    picker.setAttribute('data-thp-option-index', select.getAttribute('data-index') || 'option1');

    var header = document.createElement('div');
    header.className = 'thp-size-picker__header';
    var label = document.createElement('p');
    label.className = 'thp-size-picker__label';
    label.id = select.id + '-thp-label';
    label.textContent = prettyLabel(optionName);
    header.appendChild(label);
    picker.appendChild(header);

    var grid = document.createElement('div');
    grid.className = 'thp-size-picker__grid';
    grid.setAttribute('role', 'listbox');
    grid.setAttribute('aria-labelledby', label.id);

    var indexAttr = select.getAttribute('data-index') || 'option1';

    function refreshDisabled() {
      var selections = form ? currentSelections(form) : {};
      Array.prototype.forEach.call(grid.querySelectorAll('.thp-size-picker__btn'), function (btn) {
        var val = btn.getAttribute('data-value');
        var ok = valueAvailable(product, indexAttr, val, selections);
        // If combination logic is too strict before other options exist, fall back:
        if (!ok && Object.keys(selections).length <= 1) {
          ok = anyVariantHasValue(product, indexAttr, val);
        }
        btn.classList.toggle('is-unavailable', !ok);
        btn.disabled = !ok;
        btn.setAttribute('aria-disabled', ok ? 'false' : 'true');
      });
    }

    // Sort XXS→XL (and numeric) for display; keep select values unchanged.
    sortedOptions(select).forEach(function (opt) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'thp-size-picker__btn';
      btn.textContent = opt.textContent.trim();
      btn.setAttribute('data-value', opt.value);
      btn.setAttribute('role', 'option');
      btn.setAttribute('aria-pressed', opt.selected ? 'true' : 'false');
      if (opt.selected) btn.classList.add('is-selected');

      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        if (select.value === opt.value) return;
        select.value = opt.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        Array.prototype.forEach.call(grid.querySelectorAll('.thp-size-picker__btn'), function (b) {
          var on = b.getAttribute('data-value') === opt.value;
          b.classList.toggle('is-selected', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        // Other option pickers may need availability refresh
        document.dispatchEvent(new CustomEvent('thp:size-changed', { bubbles: true }));
      });

      grid.appendChild(btn);
    });

    picker.appendChild(grid);
    wrapper.insertBefore(picker, wrapper.firstChild);
    wrapper.classList.add('thp-size-picker--ready');
    select.dataset.thpSizeEnhanced = '1';

    select.addEventListener('change', function () {
      Array.prototype.forEach.call(grid.querySelectorAll('.thp-size-picker__btn'), function (b) {
        var on = b.getAttribute('data-value') === select.value;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      refreshDisabled();
    });

    document.addEventListener('thp:size-changed', refreshDisabled);
    refreshDisabled();
  }

  function enhanceAll() {
    document.querySelectorAll('select.single-option-selector').forEach(buildPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhanceAll);
  } else {
    enhanceAll();
  }

  // Debut sections can re-render; observe product section lightly
  document.addEventListener('shopify:section:load', enhanceAll);
})();
